import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, Clock, User, ArrowRight, Calendar } from 'lucide-react';

export default function Blog() {
  const navigate = useNavigate();

  const blogPosts = [
    {
      title: "9 Mizaç Tipini Tanıyın: Kapsamlı Rehber",
      excerpt: "Enneagram'ın dokuz temel mizaç tipini detaylı bir şekilde inceleyin. Her bir tipin özellikleri, güçlü yönleri ve gelişim alanları.",
      author: "Mizaç360 Ekibi",
      date: "15 Ocak 2025",
      readTime: "8 dk",
      category: "Enneagram",
      image: "https://images.unsplash.com/photo-1516534775068-ba3e7458af70?w=800&q=80",
      featured: true
    },
    {
      title: "Sınıfta Farklı Mizaç Tipleriyle Çalışmak",
      excerpt: "Öğretmenler için pratik öneriler: Her mizaç tipindeki öğrencilere nasıl yaklaşmalı ve onları nasıl motive etmelisiniz?",
      author: "Prof. Dr. Ayşe Yılmaz",
      date: "12 Ocak 2025",
      readTime: "6 dk",
      category: "Eğitim",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80"
    },
    {
      title: "Çocuğunuzun Mizacını Anlamak: Veliler için Rehber",
      excerpt: "Çocuğunuzun mizaç tipini anlayarak ona daha iyi rehberlik edebilir, güçlü yönlerini destekleyebilirsiniz.",
      author: "Uzm. Psk. Mehmet Demir",
      date: "10 Ocak 2025",
      readTime: "5 dk",
      category: "Aile",
      image: "https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=800&q=80"
    },
    {
      title: "Tip 1: Mükemmeliyetçi - Detaylı Analiz",
      excerpt: "Tip 1 mizaç özelliklerini derinlemesine inceleyin. Bu tipteki öğrencilerin ihtiyaçları ve öğretim stratejileri.",
      author: "Dr. Zeynep Kaya",
      date: "8 Ocak 2025",
      readTime: "7 dk",
      category: "Mizaç Tipleri",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80"
    },
    {
      title: "AI ile Kişiselleştirilmiş Eğitim",
      excerpt: "Yapay zeka destekli mizaç analizi ile her öğrenciye özel öğrenme yolları nasıl oluşturulur?",
      author: "Mizaç360 Ekibi",
      date: "5 Ocak 2025",
      readTime: "4 dk",
      category: "Teknoloji",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80"
    },
    {
      title: "Sınıf Dinamiklerini İyileştirmek",
      excerpt: "Farklı mizaç tiplerinden oluşan bir sınıfta uyumu ve işbirliğini nasıl artırabilirsiniz?",
      author: "Öğrt. Can Özdemir",
      date: "3 Ocak 2025",
      readTime: "6 dk",
      category: "Eğitim",
      image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80"
    }
  ];

  const categories = ["Tümü", "Enneagram", "Eğitim", "Aile", "Mizaç Tipleri", "Teknoloji"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center cursor-pointer" onClick={() => navigate('/')}>
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 cursor-pointer" onClick={() => navigate('/')}>Mizaç360</h1>
          </div>
          <div className="flex items-center gap-6">
            <a href="/resources" className="text-slate-600 hover:text-blue-600 transition">Kaynaklar</a>
            <a href="/blog" className="text-slate-700 hover:text-blue-600 font-medium transition">Blog</a>
            <a href="/news" className="text-slate-600 hover:text-blue-600 transition">Haberler & Etkinlikler</a>
            <a href="/about" className="text-slate-600 hover:text-blue-600 transition">Hakkımızda</a>
            <Button variant="outline" onClick={() => navigate('/')}>Ana Sayfa</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-100 text-purple-700">İçgörü & Bilgi</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Mizaç360 Blog
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Enneagram, mizaç tipleri ve eğitim stratejileri hakkında uzman görüşleri ve pratik öneriler
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {categories.map((cat, idx) => (
            <Button 
              key={idx}
              variant={idx === 0 ? "default" : "outline"}
              size="sm"
              className={idx === 0 ? "bg-gradient-to-r from-blue-600 to-emerald-600" : ""}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Featured Post */}
        {blogPosts[0].featured && (
          <Card className="shadow-xl border-none mb-12 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="h-64 md:h-auto">
                <img 
                  src={blogPosts[0].image} 
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <Badge className="w-fit mb-4 bg-blue-100 text-blue-700">{blogPosts[0].category}</Badge>
                <h3 className="text-3xl font-bold text-slate-800 mb-4">{blogPosts[0].title}</h3>
                <p className="text-slate-600 mb-6">{blogPosts[0].excerpt}</p>
                <div className="flex items-center gap-6 text-sm text-slate-500 mb-6">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{blogPosts[0].author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{blogPosts[0].date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{blogPosts[0].readTime}</span>
                  </div>
                </div>
                <Button className="w-fit bg-gradient-to-r from-blue-600 to-emerald-600 group">
                  Devamını Oku
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.slice(1).map((post, idx) => (
            <Card key={idx} className="shadow-lg border-none hover:shadow-xl transition-shadow overflow-hidden group cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardHeader>
                <Badge className="w-fit mb-2 text-xs" variant="outline">{post.category}</Badge>
                <CardTitle className="text-lg leading-tight">{post.title}</CardTitle>
                <CardDescription className="text-sm mt-2">{post.excerpt}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="group/btn p-0 h-auto">
                  Oku
                  <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <Button variant="outline" size="lg">
            Daha Fazla Yükle
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-300">© 2025 Mizaç360. Tüm hakları saklıdır.</p>
          <p className="text-sm text-slate-400 mt-2">
            Bilimsel kaynaklar: İsmail Acarkan, Ziya Selçuk, Eduen.com.tr, The Enneagram Institute, Integrative9
          </p>
        </div>
      </footer>
    </div>
  );
}
